import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CircleUserRound, LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "../../lib/cn";
import {
  isNavigationItemActive,
  type NavigationItem,
} from "./navigationConfig";

export interface UserMenuProps {
  currentPath: string;
  items: NavigationItem[];
  onLogout: () => void;
  user?: {
    username?: string;
    role?: string;
  } | null;
}

function formatRoleLabel(role?: string): string {
  if (!role) {
    return "No role";
  }

  return role.charAt(0).toUpperCase() + role.slice(1);
}

const UserMenu = ({ currentPath, items, onLogout, user }: UserMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleMenu = useCallback(() => {
    setIsOpen((current) => !current);
  }, []);

  const handleLogout = useCallback(() => {
    closeMenu();
    onLogout();
  }, [closeMenu, onLogout]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [closeMenu]);

  const accountLabel = useMemo(() => user?.username || "User", [user?.username]);
  const roleLabel = useMemo(() => formatRoleLabel(user?.role), [user?.role]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={toggleMenu}
        className="flex items-center gap-3 rounded-full bg-white/60 p-1.5 pr-3 text-left shadow-sm backdrop-blur-sm transition-colors hover:bg-white dark:bg-gray-900/70 dark:hover:bg-gray-900"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-orange-300">
          <CircleUserRound className="h-5 w-5" />
        </span>
        <span className="hidden sm:block">
          <span className="block text-sm font-medium text-gray-800 dark:text-gray-100">{accountLabel}</span>
          <span className="block text-xs text-gray-500 dark:text-gray-400">{roleLabel}</span>
        </span>
      </button>

      {isOpen ? (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-2xl border border-orange-100 bg-white/95 shadow-lg backdrop-blur-sm dark:border-gray-800 dark:bg-gray-900/95"
          role="menu"
        >
          <div className="border-b border-orange-100 px-4 py-3 dark:border-gray-800">
            <p className="text-sm font-medium text-gray-900 dark:text-white">{accountLabel}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{roleLabel}</p>
          </div>

          <div className="py-1">
            {items.map((item) => {
              const Icon = item.icon;
              const isActive = isNavigationItemActive(item, currentPath);

              return (
                <Link
                  key={item.id}
                  to={item.to}
                  onClick={closeMenu}
                  className={cn(
                    "flex items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                    isActive
                      ? "bg-orange-50 text-orange-600 dark:bg-orange-500/15 dark:text-orange-300"
                      : "text-gray-700 hover:bg-orange-50 dark:text-gray-200 dark:hover:bg-gray-800",
                  )}
                  role="menuitem"
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>

          <div className="border-t border-orange-100 p-1 dark:border-gray-800">
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10"
              role="menuitem"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign out</span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default UserMenu;
